import { useQuery } from '@apollo/client';
import { LiveTv, Security } from '@mui/icons-material';
import { Alert, Box, Button, Chip, Stack } from '@mui/material';
import Hls from 'hls.js';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { Tooltip } from '~/components/Tooltip';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { LiveChannelsDocument } from './queries/list.generated';

export const ChannelDetail = () => {
  const { t } = useTranslation();
  const { channelId } = useParams<{ channelId: string }>();
  const { currentNetwork } = useAppContext();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    data,
    loading,
    error: queryError,
  } = useQuery(LiveChannelsDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const channel = (data?.network?.allChannels ?? []).find((ch) => ch.id === Number(channelId));

  const handleTrustCert = () => {
    const transcodeHost = currentNetwork.settings.transcodeServerHost;
    if (!transcodeHost) {
      setError(t('pages.live_channels.no_transcode_server'));
      return;
    }
    // Open in new window so they can accept the cert
    window.open(
      `https://${transcodeHost}:8000/trust-certificate`,
      '_blank',
      'width=600,height=400',
    );
  };

  useEffect(() => {
    if (!channel || !videoRef.current) return;

    const video = videoRef.current;
    const streamUrl = getStreamUrl(channel.id, currentNetwork.settings.transcodeServerHost || '');

    // Check if HLS is supported
    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
      });

      hlsRef.current = hls;

      hls.loadSource(streamUrl);
      hls.attachMedia(video);

      // Clear error when playback starts
      const handlePlay = () => {
        setError(null);
      };
      video.addEventListener('play', handlePlay);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch((err) => {
          console.error('Autoplay failed:', err);
          setError(t('pages.live_channels.autoplay_failed'));
        });
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              setError(t('pages.live_channels.network_error'));
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              setError(t('pages.live_channels.media_error'));
              hls.recoverMediaError();
              break;
            default:
              setError(t('pages.live_channels.fatal_error'));
              hls.destroy();
              break;
          }
        }
      });

      return () => {
        video.removeEventListener('play', handlePlay);
        hls.destroy();
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      video.src = streamUrl;
      video.addEventListener('loadedmetadata', () => {
        video.play().catch((err) => {
          console.error('Autoplay failed:', err);
          setError(t('pages.live_channels.autoplay_failed'));
        });
      });
    } else {
      setError(t('pages.live_channels.hls_not_supported'));
    }
  }, [channel, currentNetwork.settings.transcodeServerHost, t]);

  if ((!loading && !data) || queryError) return <Navigate replace to="/not-found" />;

  return (
    <>
      <Helmet title={channel?.name ?? t('pages.live_channels.loading')} />
      <Toolbar
        titleIcon={
          channel?.thumbnailFile.uri ? (
            <img
              src={channel.thumbnailFile.uri}
              alt={channel.name}
              style={{ width: 35, height: 35, objectFit: 'contain', borderRadius: 4 }}
            />
          ) : (
            <LiveTv />
          )
        }
        titleText={channel?.name ?? t('pages.live_channels.loading')}
        actions={
          <Tooltip title={t('pages.live_channels.trust_cert_tooltip')} placement="bottom">
            <Button
              variant="outlined"
              startIcon={<Security />}
              onClick={handleTrustCert}
              disabled={!currentNetwork.settings.transcodeServerHost}
            >
              {t('pages.live_channels.trust_cert_button')}
            </Button>
          </Tooltip>
        }
      />
      <RouterBreadcrumbs />
      <PageContainer>
        <LoadingPane in={loading && !data} size={80} thickness={4}>
          {!channel ? (
            <Alert severity="error">{t('pages.live_channels.channel_not_found_message')}</Alert>
          ) : (
            <Box>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '56.25%', // 16:9 aspect ratio
                  backgroundColor: 'common.black',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: 3,
                }}
              >
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  ref={videoRef}
                  controls
                  controlsList="noplaybackrate nodownload noremoteplayback"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />

                {/* Channel info overlay */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    pointerEvents: 'none',
                  }}
                >
                  {channel.number && (
                    <Chip
                      label={t('pages.live_channels.channel_abbreviation', {
                        number: channel.number,
                      })}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  )}
                  {channel.source && (
                    <Chip
                      label={channel.source}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Box>
          )}
        </LoadingPane>
      </PageContainer>
    </>
  );
};

function getStreamUrl(channelId: number, transcodeHost: string) {
  return `https://${transcodeHost}:8000/channel_${channelId}/playlist.m3u8`;
}
